import { useState } from "react";
import Link from "next/link";
import { Trans } from "next-i18next";
import { getCookie } from "cookies-next";
import { useConsentStore } from "@/libs/consent/consentStore";
import { CONSENT_COOKIE_NAME } from "@/libs/consent/constants";
import { useHydrated } from "@/hooks/useHydrated";
import { useTrans } from "@/utils/useTrans";
import { cn } from "@/libs/utils";

// IP 기반 대략 위치로 "근처 매장"을 자동 정렬한다는 고지(notice) 배너.
// opt-in 게이트가 아니라 dismiss 가능한 안내 — 그래서 기존 전역 consent 접점과
// 중복되면 생략한다(banner stacking 방지).
//
// 노출 규칙(docs/consent.md 시그널 사용):
//   - 저장된 consent 쿠키가 있음(재방문/동의 완료)     → 생략(이미 프라이버시 접점 지남)
//   - 쿠키 배너가 노출 중(showBanner=true, GDPR/US 등) → 생략(그 순간을 기존 배너가 담당)
//   - 그 외(KR/JP 등 no-banner 지역)                   → 1회 노출 + 자체 dismiss 영속
const LS_DISMISSED = "wte_ip_notice_dismissed";

function readDismissed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(LS_DISMISSED) === "1";
  } catch {
    // private mode 등 — 읽기 실패 시 노출 허용(고지 목적 우선).
    return false;
  }
}

function readSavedConsent(): boolean {
  if (typeof window === "undefined") return true; // 서버 렌더에선 미노출 쪽으로.
  try {
    return !!getCookie(CONSENT_COOKIE_NAME);
  } catch {
    return false;
  }
}

export default function LocationNoticeBanner({
  className,
}: {
  className?: string;
}) {
  const { t } = useTrans(["where-to-try"]);
  const initialized = useConsentStore((s) => s.initialized);
  const showBanner = useConsentStore((s) => s.showBanner);

  // 서버 렌더·하이드레이션 렌더에서는 false → 미노출로 통일(hydration mismatch 방지).
  // ⚠️ "첫 클라 렌더에서 항상 false" 가 **아니다** — SPA 이동은 하이드레이션이 없어 즉시
  // true 다. 이전 `useMounted`(useState+useEffect)와 갈리는 유일한 지점으로, 그쪽은 SPA
  // 이동에도 한 프레임 배너를 숨겼다. 계약은 `__tests__/hooks/useHydrated.test.tsx`,
  // 이 컴포넌트의 두 경로는 `__tests__/v2-components/location-notice-banner.test.tsx` 가 잠근다.
  //
  // 쿠키/localStorage 스냅샷은 최초 1회만 읽는다(lazy initializer — effect 불필요).
  const hydrated = useHydrated();
  const [savedConsent] = useState(readSavedConsent);
  const [dismissed, setDismissed] = useState(readDismissed);

  const show =
    hydrated && initialized && !savedConsent && !showBanner && !dismissed;

  if (!show) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(LS_DISMISSED, "1");
    } catch {
      /* private mode 등 — 무시 */
    }
  };

  return (
    // Figma: 모바일=세로 스택(텍스트 → 버튼 우하단), 데스크톱(smd+)=가로 1행(텍스트 flex-1 + 버튼 우측).
    // 데스크톱 높이 80px = py-5(20) + 버튼 40 + py-5(20). 모바일 p-4(16) + gap-3(12).
    <div
      className={cn(
        "flex w-full flex-col items-end gap-3 rounded-lg bg-coolgray-10 p-4",
        "smd:flex-row smd:items-center smd:gap-3 smd:px-6 smd:py-5",
        className,
      )}
    >
      <div className="flex w-full items-center gap-3 smd:min-w-0 smd:flex-1">
        <InfoIcon className="size-6 shrink-0 text-orange-50" />
        <p className="min-w-0 text-sm leading-[18px] tracking-[0.25px] text-coolgray-90">
          <Trans
            t={t}
            i18nKey="We use your approximate location (via IP) to show nearby venues. See our <privacy>Privacy Policy</privacy>."
            components={{
              privacy: (
                <Link
                  href="/legals/privacy-policy/"
                  target="_blank"
                  className="text-blue-60 underline underline-offset-2"
                />
              ),
            }}
          />
        </p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="flex h-10 shrink-0 items-center justify-center rounded-full bg-coolgray-90 px-6 text-xs font-600 tracking-[0.1px] text-white transition-colors hover:bg-coolgray-hover-90"
      >
        {t("Got it")}
      </button>
    </div>
  );
}

// Figma Icons/info-24px: 오렌지(#EB6200 = orange-50) 채운 원 + 흰색 "i".
function InfoIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path
        d="M12 11v5.5"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="12" cy="7.75" r="1.25" fill="#fff" />
    </svg>
  );
}
